import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/#stories", label: "Stories" },
  { to: "/#about", label: "About" },
  { to: "/#brands", label: "Brands" },
  { to: "/press", label: "Press", route: true },
  { to: "/#collaborate", label: "Collaborate" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const handleAnchor = (e, to) => {
    if (!to.startsWith("/#")) return;
    if (location.pathname !== "/") return; // allow default navigation
    e.preventDefault();
    const id = to.replace("/#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // When user clicks a route Link for the page they're already on, react-router
  // does nothing — so the page stays scrolled where it was. Force-scroll to top.
  const handleSameRouteScrollTop = (e, to) => {
    if (location.pathname === to && !location.hash) {
      e.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-[#fdfbf7]/95 backdrop-blur-sm border-b border-black/5"
          : "bg-[#fdfbf7]"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-home-logo"
          onClick={(e) => handleSameRouteScrollTop(e, "/")}
          className="flex items-center"
        >
          <img
            src="/logo-mark.png"
            alt="Stories by Aradhana"
            width="132"
            height="56"
            fetchPriority="high"
            decoding="async"
            className="h-8 md:h-10 w-auto"
          />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) =>
            l.route ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={(e) => handleSameRouteScrollTop(e, l.to)}
                className="sba-navlink font-sans text-sm text-[#1a1a1a] tracking-wide"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="sba-navlink font-sans text-sm text-[#1a1a1a] tracking-wide"
              >
                {l.label}
              </a>
            )
          )}

          <NavLink
            to="/foundation"
            data-testid="nav-link-foundation"
            onClick={(e) => handleSameRouteScrollTop(e, "/foundation")}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 rounded-full border border-[#2d4a22] px-4 py-2 text-xs font-sans tracking-[0.15em] uppercase transition-colors duration-300 ${
                isActive
                  ? "bg-[#2d4a22] text-[#fdfbf7]"
                  : "text-[#2d4a22] hover:bg-[#2d4a22] hover:text-[#fdfbf7]"
              }`
            }
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2d4a22] group-hover:bg-[#fdfbf7]" />
            Foundation
          </NavLink>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 -mr-2 text-[#1a1a1a]"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-black/10 bg-[#fdfbf7]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) =>
              l.route ? (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={(e) => handleSameRouteScrollTop(e, l.to)}
                  className="font-sans text-base text-[#1a1a1a]"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.to}
                  href={l.to}
                  onClick={(e) => handleAnchor(e, l.to)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-sans text-base text-[#1a1a1a]"
                >
                  {l.label}
                </a>
              )
            )}
            <NavLink
              to="/foundation"
              data-testid="nav-mobile-link-foundation"
              onClick={(e) => handleSameRouteScrollTop(e, "/foundation")}
              className="inline-flex items-center justify-center gap-2 self-start rounded-full border border-[#2d4a22] px-4 py-2 text-xs font-sans tracking-[0.15em] uppercase text-[#2d4a22]"
            >
              Foundation
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
}
